import clsx from "clsx";
import React, { Fragment, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useToggle } from "react-use";

import {
  WheelPartItem,
  WheelStockItem,
} from "../../../queries/useSearchWheelsByVehicle";
import { useNullableDealerAccountInfo } from "../../../stores/hooks/useNullableDealerAccountInfo";
import { Button } from "../../../ui/Button/Button";
import { Dropdown } from "../../../ui/Dropdown";
import { TimeAgo } from "../../../ui/TimeAgo/TimeAgo";
import { Toggle } from "../../../ui/Toggle";
import { ChevronRightIcon } from "../../../ui/icons/ChevronRightIcon";
import {
  AVAILABILITY_LABELS,
  buildRealTimeRows,
  countRealTimeRows,
  hasProviderBreakdown,
} from "../../Stock/realTimeStock";
import { TireLinkLogo } from "../../TireLinkLogo/TireLinkLogo";
import { StockLevels } from "./WheelStockLevels";
import { StockRecord } from "./WheelStockRecord";

type WheelStockProps = {
  allStock: WheelStockItem[];
  dlrStock: WheelStockItem[];
  dropdownId: string;
  isRestricted: boolean;
  part: WheelPartItem;
  rltStock: WheelStockItem[];
};

export const countStock = (stock: WheelStockItem[]): number =>
  stock.reduce((acc, { stockQty }) => acc + Number(stockQty), 0);

export const filterStock = (
  stock: WheelStockItem[],
  locationType: string,
): WheelStockItem[] =>
  stock.filter(
    ({ stockLocationType, stockQty }) =>
      stockLocationType === locationType && Number(stockQty) > 0,
  );

export const formatCount = (
  count: number | string,
  maxCount: number = 20,
): string => {
  const value = Number(count);
  if (value > maxCount) {
    return "20+";
  }
  return count.toString();
};

export const WheelStock: FunctionComponent<WheelStockProps> = ({
  allStock,
  dlrStock,
  dropdownId,
  isRestricted,
  part,
  rltStock,
}) => {
  const { t } = useTranslation();
  const [open, toggle] = useToggle(false);
  const dealer = useNullableDealerAccountInfo();

  const locStock = filterStock(allStock, "LOC");

  const dlrStockCount = countStock(dlrStock);
  const locStockCount = countStock(locStock);
  const rtlStockCount = countStock(rltStock);
  const realTimeRows = buildRealTimeRows(rltStock);
  const providerBreakdown = rltStock.some(hasProviderBreakdown);
  const { tdg: tdgStockCount, tireLink: tireLinkStockCount } =
    countRealTimeRows(realTimeRows);
  const buttonId = `${dropdownId}-button`;

  if (isRestricted && !locStockCount) {
    return null;
  }

  return (
    <div className="bg-paper-2 text-ink bg-opacity-90 text-sm tw-stock">
      <Toggle disabled={!allStock.length || isRestricted} onToggle={toggle}>
        <Button
          className={clsx(
            "px-4 md:px-8 h-10 rounded-none flex w-full justify-between",
            isRestricted && "pointer-events-none",
          )}
          aria-label="wheelStockToggle"
          disabled={!allStock.length}
          id={buttonId}
        >
          <span className="text-left flex flex-1 whitespace-nowrap truncate gap-1 items-center">
            {[
              !isRestricted &&
                rltStock.length > 0 &&
                (!providerBreakdown || tireLinkStockCount > 0) && (
                  <>
                    <TireLinkLogo className="h-4 -translate-y-[0.15em] -ml-1 -mr-1" />
                    :{" "}
                    {formatCount(
                      providerBreakdown ? tireLinkStockCount : rtlStockCount,
                    )}
                  </>
                ),
              !isRestricted && tdgStockCount > 0 && (
                <>Tire Discounter: {formatCount(tdgStockCount)}</>
              ),
              locStockCount > 0 && (
                <>
                  {t("dealerWheelTile.wheels", {
                    count: locStockCount,
                    displayCount: formatCount(locStockCount),
                  })}
                  <span>
                    {" "}
                    {t("dealerWheelTile.inLocation", {
                      count: locStock.length,
                    })}
                  </span>
                </>
              ),
              !isRestricted && dlrStockCount > 0 && (
                <>
                  {t("dealerWheelTile.wheels", {
                    count: dlrStockCount,
                    displayCount: formatCount(dlrStockCount),
                  })}
                </>
              ),
            ]
              .filter(Boolean)
              .map((item, key) => (
                <Fragment key={key}>
                  {!!key && <span className="opacity-40">|</span>}
                  {item}
                </Fragment>
              ))}
          </span>
          {!isRestricted && (
            <ChevronRightIcon
              className={clsx(
                "transform transition-transform duration-300",
                open ? "rotate-90" : "-rotate-90",
              )}
            />
          )}
        </Button>
        <Dropdown
          aria-labelledby={buttonId}
          blockLevel
          className="bg-paper-2 rounded-t-3xl rounded-b-none z-10 py-2 max-h-56 overflow-y-auto"
          id={dropdownId}
          placement="top"
          role="menu"
        >
          {dlrStock.map(({ stockLocationId, stockLocationName, stockQty }) => (
            <StockRecord key={stockLocationId}>
              {stockLocationName}
              {t("dealerWheelTile.wheels", {
                count: Number(stockQty),
                displayCount: formatCount(stockQty),
              })}
              {t("dealerWheelTile.availableStock")}
            </StockRecord>
          ))}
          {realTimeRows.map((row) =>
            row.kind === "tireLink" ? (
              <StockRecord key={row.key}>
                <>TireLink</>
                <>{formatCount(row.local)} Local DC</>
                <>{formatCount(row.localPlus)} Local+</>
              </StockRecord>
            ) : (
              <StockRecord key={row.key}>
                {row.name}
                {t("dealerWheelTile.wheels", {
                  count: row.qty,
                  displayCount: formatCount(row.qty),
                })}
                <>{AVAILABILITY_LABELS[row.availability] ?? ""}</>
              </StockRecord>
            ),
          )}
          {(dlrStock.length || rltStock.length) > 0 && locStock.length > 0 && (
            <hr className="mx-4 md:mx-8 my-1 border-ink opacity-40" />
          )}
          {locStock.map(
            ({
              minutesSinceLastStockUpdate,
              stockLocationId,
              stockLocationName,
              stockLocationWebsite,
              stockQty,
            }) => (
              <StockRecord key={stockLocationId} link={stockLocationWebsite}>
                {stockLocationName}
                {t("dealerWheelTile.wheels", {
                  count: Number(stockQty),
                  displayCount: formatCount(stockQty),
                })}
                <TimeAgo minutes={minutesSinceLastStockUpdate} />
              </StockRecord>
            ),
          )}
          {dealer?.dealerEnableNationalStockCheck && (
            <>
              <hr className="mx-4 md:mx-8 my-1 border-ink opacity-40" />
              <StockLevels part={part} />
            </>
          )}
        </Dropdown>
      </Toggle>
    </div>
  );
};
